import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";

const MODES = [
  { key: "after_discount", label: "Discount After Discount" },
  { key: "direct", label: "Direct Discount" },
];

export const CustomerDiscountDialog = ({ customer, open, onOpenChange, onSaved }) => {
  const [companies, setCompanies] = useState([]);
  const [mode, setMode] = useState("after_discount");
  const [rates, setRates] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;
    api.get("/masters/company").then(({ data }) => setCompanies(data.map((c) => c.name))).catch(() => {});
    api
      .get(`/customers/${encodeURIComponent(customer.name)}/discounts`)
      .then(({ data }) => {
        setMode(data.mode || "after_discount");
        setRates(Object.fromEntries((data.rates || []).map((r) => [r.company, String(r.pct)])));
      })
      .catch(() => {});
  }, [open, customer]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        mode,
        rates: Object.entries(rates)
          .map(([company, pct]) => ({ company, pct: parseFloat(pct) || 0 }))
          .filter((r) => r.pct),
      };
      await api.put(`/customers/${encodeURIComponent(customer.name)}/discounts`, payload);
      toast.success("Company wise discount saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl font-extrabold tracking-tight">
            Company wise discount
          </DialogTitle>
          <DialogDescription>
            Saved rates for {customer?.name} — Hisab fills these in with one click. The rate is the total
            discount you want; whatever the bill already gave is knocked off.
          </DialogDescription>
        </DialogHeader>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Formula</p>
          <div className="mt-2 flex gap-2">
            {MODES.map((m) => (
              <Button
                key={m.key}
                type="button"
                data-testid={`customer-discount-mode-${m.key}`}
                size="sm"
                variant={mode === m.key ? "default" : "outline"}
                className={`rounded-sm ${mode === m.key ? "bg-[var(--mod-primary)] text-white" : ""}`}
                onClick={() => setMode(m.key)}
              >
                {m.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-4 max-h-72 overflow-y-auto border border-zinc-200">
          {companies.length === 0 ? (
            <p className="p-4 text-sm text-zinc-500">Add companies in Admin → Company Master first.</p>
          ) : (
            companies.map((c) => (
              <div key={c} className="flex items-center justify-between gap-3 border-b border-zinc-100 px-3 py-2 last:border-b-0">
                <span className="text-sm font-semibold uppercase">{c}</span>
                <div className="flex items-center gap-2">
                  <Input
                    data-testid={`customer-discount-pct-${c}`}
                    type="number"
                    step="any"
                    className="h-9 w-24 rounded-sm"
                    placeholder="0"
                    value={rates[c] ?? ""}
                    onChange={(e) => setRates((r) => ({ ...r, [c]: e.target.value }))}
                  />
                  <span className="text-xs text-zinc-500">%</span>
                </div>
              </div>
            ))
          )}
        </div>

        <Button
          data-testid="customer-discount-save-button"
          className="mt-4 w-full rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
          disabled={saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save discount rates"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
